// 보호된 라우트 컴포넌트 (인증 확인 후 렌더링)

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ROUTES } from '../constants/routes';
import { useAuthStore } from '../store/authStore';

const ProtectedLayout: React.FC = () => {
  const location = useLocation();
  const { fetchUser, clearAuth, isAuthenticated, isInitialized } = useAuthStore();
  const [isVerifying, setIsVerifying] = useState(true);

  /**
   * 보호된 라우트 접근 시 서버에서 인증 상태 재확인
   * - App.tsx에서 이미 initializeAuth()를 호출했지만,
   *   초기화가 완료되지 않았거나 인증 상태가 불확실한 경우 추가 확인
   * - 새로고침 시에도 현재 경로를 유지하기 위해 인증 확인만 수행
   */
  useEffect(() => {
    // App.tsx에서 이미 초기화가 완료되었고 인증된 상태면 추가 확인 불필요
    if (isInitialized && isAuthenticated) {
      setIsVerifying(false);
      return;
    }

    // 초기화가 완료되지 않았으면 대기 (App.tsx에서 처리 중)
    if (!isInitialized) {
      return;
    }

    // 초기화는 완료되었지만 인증되지 않은 경우에만 추가 확인
    // (쿠키 만료 등으로 인증이 실패했을 수 있음)
    if (isInitialized && !isAuthenticated) {
      const verifyAuth = async () => {
        try {
          await fetchUser();
          setIsVerifying(false);
        } catch (error) {
          // 서버에서 인증 실패 시 자동으로 로그아웃 처리
          console.error('라우트 접근 시 인증 확인 실패 - 자동 로그아웃:', error);
          clearAuth();
          setIsVerifying(false);
        }
      };

      verifyAuth();
    }
  }, [isInitialized, isAuthenticated, fetchUser, clearAuth]);

  // 초기화가 완료되지 않았거나 인증 확인 중이면 로딩 표시
  // (App.tsx에서도 로딩을 보여주지만, 이중 체크로 안전성 확보)
  if (!isInitialized || isVerifying) {
    return <LoadingSpinner message="인증 확인 중..." fullScreen />;
  }

  // 인증 확인 후 인증되지 않았으면 로그인 페이지로 리다이렉트
  // 단, 현재 경로를 state에 저장하여 로그인 후 복귀 가능하도록 함
  if (!isAuthenticated) {
    console.log('🚫 인증되지 않음, 로그인 페이지로 리다이렉트:', {
      currentPath: location.pathname,
      from: location.state?.from?.pathname,
    });
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />;
  }

  // 인증 확인 완료 후 Layout 렌더링 (현재 경로 유지)
  console.log('✅ ProtectedLayout 렌더링:', {
    currentPath: location.pathname,
    isAuthenticated,
    isInitialized,
  });
  return <Layout />;
};

export default ProtectedLayout;
